import { useAuth } from '../../utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { rooms as roomsApi } from '../../utils/api';
import api from '../../utils/api';
import { Plus, Edit2, Tag, Clock, ChevronDown, ChevronUp, Trash2, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import BrandIcon from '../../components/BrandIcon';

const DAYS = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];

// ── Modale infos salle ────────────────────────────────────────
function RoomFormModal({ selected, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: selected?.name||'', description: selected?.description||'',
    story: selected?.story||'', difficulty: selected?.difficulty||3,
    minPlayers: selected?.min_players||2, maxPlayers: selected?.max_players||6,
    durationMin: selected?.duration_min||60, imageUrl: selected?.image_url||'',
    isActive: selected?.is_active??true,
    showInLive: selected?.show_in_live??true,
    isAddon: selected?.is_addon??false,
  });
  const f = k => ({ value: form[k], onChange: e => setForm(p=>({...p,[k]:e.target.value})) });
  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    try {
      selected ? await roomsApi.update(selected.id, { ...form, showInLive: form.showInLive, isAddon: form.isAddon }) : await roomsApi.create(form);
      toast.success(selected?'Salle mise à jour':'Salle créée');
      onSaved(); onClose();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:580}}>
        <h3 className="modal-title">{selected?'Modifier la salle':'Nouvelle salle'}</h3>
        <form onSubmit={submit}>
          <div className="form-group"><label className="form-label">Nom *</label>
            <input className="input" required {...f('name')} placeholder="La Chambre Maudite"/></div>
          <div className="form-group"><label className="form-label">Description courte</label>
            <textarea className="input" rows={2} {...f('description')} style={{resize:'vertical'}}/></div>
          <div className="form-group"><label className="form-label">Histoire / univers</label>
            <textarea className="input" rows={3} {...f('story')} style={{resize:'vertical'}}/></div>
          <div className="form-row-3">
            <div className="form-group"><label className="form-label">Durée (min)</label>
              <input type="number" className="input" min={15} max={180} {...f('durationMin')}/></div>
            <div className="form-group"><label className="form-label">Min joueurs</label>
              <input type="number" className="input" min={1} max={20} {...f('minPlayers')}/></div>
            <div className="form-group"><label className="form-label">Max joueurs</label>
              <input type="number" className="input" min={1} max={20} {...f('maxPlayers')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Difficulté</label>
              <div style={{display:'flex',gap:6,marginTop:4}}>
                {[1,2,3,4,5].map(n=>(
                  <button key={n} type="button" onClick={()=>setForm(p=>({...p,difficulty:n}))}
                    style={{width:36,height:36,borderRadius:8,border:'1px solid var(--border)',cursor:'pointer',
                      background:form.difficulty>=n?'var(--accent-glow)':'var(--bg-elevated)',
                      color:form.difficulty>=n?'var(--accent-light)':'var(--text-3)',fontSize:16}}>★</button>
                ))}
              </div>
            </div>
            <div className="form-group"><label className="form-label">Statut</label>
              <div style={{display:'flex',gap:8}}>
                {[{v:true,l:'Active'},{v:false,l:'Désactivée'}].map(({v,l})=>(
                  <button key={String(v)} type="button"
                    onClick={()=>setForm(p=>({...p,isActive:v}))}
                    style={{flex:1,padding:'9px',borderRadius:8,border:'none',cursor:'pointer',
                      fontFamily:'inherit',fontSize:13,fontWeight:600,
                      background:form.isActive===v?(v?'rgba(0,210,180,0.15)':'rgba(255,77,109,0.15)'):'var(--bg-elevated)',
                      color:form.isActive===v?(v?'var(--teal)':'var(--red)'):'var(--text-3)',
                      border:`1px solid ${form.isActive===v?(v?'var(--teal)':'var(--red)'):'var(--border)'}`}}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="form-group"><label className="form-label">URL image</label>
            <input type="text" className="input" {...f('imageUrl')} placeholder="https://..."/></div>
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>
            {[
              {key:'showInLive', label:'Afficher dans le tableau de bord temps réel'},
              {key:'isAddon',    label:'Salle add-on (sans créneau horaire fixe)'},
            ].map(({key,label}) => (
              <div key={key} onClick={e=>{e.stopPropagation();setForm(p=>({...p,[key]:!p[key]}))}}
                style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                  padding:'10px 14px',borderRadius:10,cursor:'pointer',
                  background:'var(--bg-elevated)',border:'1px solid var(--border)'}}>
                <span style={{fontSize:13,color:'var(--text-1)'}}>{label}</span>
                <div style={{width:38,height:22,borderRadius:11,position:'relative',transition:'background 0.2s',
                  background:form[key]?'var(--accent)':'var(--border)'}}>
                  <div style={{position:'absolute',top:3,left:form[key]?18:3,width:16,height:16,
                    borderRadius:'50%',background:'white',transition:'left 0.2s'}}/>
                </div>
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="button" className="btn btn-primary" onClick={submit}>{selected?'Mettre à jour':'Créer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Gestionnaire de tarifs dégressifs ─────────────────────────
function PriceManager({ room, onSaved }) {
  const [globalProfiles, setGlobalProfiles] = useState([]);
  // Structure : [{profileId, profileName, tiers:[{minPlayers,maxPlayers,pricePerPerson,priceFlat,mode}]}]
  const [profilePrices, setProfilePrices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showNewProfile, setShowNewProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');

  useEffect(() => {
    api.get('/payments/price-profiles').then(r => setGlobalProfiles(r.data.profiles||[])).catch(()=>{});
    // Construire depuis les données room
    const byProfile = {};
    for (const p of (room.prices||[]).filter(Boolean)) {
      if (!byProfile[p.price_profile_id]) {
        byProfile[p.price_profile_id] = { profileId:p.price_profile_id, profileName:p.profile_name, tiers:[] };
      }
      for (const tier of (p.tiers||[])) {
        byProfile[p.price_profile_id].tiers.push({
          minPlayers: tier.min_players||1,
          maxPlayers: tier.max_players||'',
          pricePerPerson: tier.price_per_person||'',
          priceFlat: tier.price_flat||'',
          mode: tier.price_flat ? 'flat' : 'per_person',
        });
      }
      if (byProfile[p.price_profile_id].tiers.length === 0) {
        byProfile[p.price_profile_id].tiers.push({ minPlayers:1, maxPlayers:'', pricePerPerson:'', priceFlat:'', mode:'per_person' });
      }
    }
    setProfilePrices(Object.values(byProfile));
  }, [room]);

  const addProfile = async profile => {
    if (profilePrices.find(p=>p.profileId===profile.id)) return;
    // Charger les tranches par défaut du profil
    let defaultTiers = [{ minPlayers:1, maxPlayers:'', pricePerPerson:'', priceFlat:'', mode:'per_person' }];
    try {
      const r = await api.get('/payments/price-profiles/'+profile.id+'/tiers');
      if (r.data.tiers?.length > 0) {
        defaultTiers = r.data.tiers.map(t => ({
          minPlayers: t.min_players,
          maxPlayers: t.max_players || '',
          pricePerPerson: t.price_per_person || '',
          priceFlat: t.price_flat || '',
          mode: t.price_flat ? 'flat' : 'per_person',
        }));
      }
    } catch {}
    setProfilePrices(prev=>[...prev,{profileId:profile.id,profileName:profile.name,tiers:defaultTiers}]);
  };

  const removeProfile = profileId => setProfilePrices(prev=>prev.filter(p=>p.profileId!==profileId));

  const addTier = profileId => setProfilePrices(prev=>prev.map(p=>
    p.profileId===profileId ? {...p, tiers:[...p.tiers,{minPlayers:'',maxPlayers:'',pricePerPerson:'',priceFlat:'',mode:'per_person'}]} : p
  ));

  const removeTier = (profileId,idx) => setProfilePrices(prev=>prev.map(p=>
    p.profileId===profileId ? {...p, tiers:p.tiers.filter((_,i)=>i!==idx)} : p
  ));

  const updateTier = (profileId,idx,key,val) => setProfilePrices(prev=>prev.map(p=>
    p.profileId===profileId ? {...p, tiers:p.tiers.map((t,i)=>i===idx?{...t,[key]:val}:t)} : p
  ));

  const createAndAddProfile = async () => {
    if (!newProfileName.trim()) return toast.error('Nom requis');
    try {
      const r = await api.post('/payments/price-profiles', {name:newProfileName});
      const created = r.data.profile;
      setGlobalProfiles(prev=>[...prev,created]);
      addProfile(created);
      setNewProfileName(''); setShowNewProfile(false);
      toast.success(`Profil "${created.name}" créé`);
    } catch { toast.error('Erreur'); }
  };

  // Note : fonction actuellement non déclenchée par un bouton visible (code
  // orphelin) — le window.confirm() a été retiré comme partout ailleurs ;
  // si un bouton est un jour ajouté pour l'appeler, passer par ConfirmModal
  // (voir components/ConfirmModal.jsx) plutôt que de réintroduire un confirm() natif.
  const resetFromProfile = async (profileId) => {
    try {
      const r = await api.get('/payments/price-profiles/'+profileId+'/tiers');
      if (r.data.tiers?.length > 0) {
        const defaultTiers = r.data.tiers.map(t => ({
          minPlayers: t.min_players,
          maxPlayers: t.max_players || '',
          pricePerPerson: t.price_per_person || '',
          priceFlat: t.price_flat || '',
          mode: t.price_flat ? 'flat' : 'per_person',
        }));
        setProfilePrices(prev => prev.map(p =>
          p.profileId === profileId ? { ...p, tiers: defaultTiers } : p
        ));
        toast.success('Tarifs réinitialisés depuis le profil');
      }
    } catch { toast.error('Erreur'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = profilePrices.map(p=>({
        priceProfileId: p.profileId,
        tiers: p.tiers.map(t=>({
          minPlayers: parseInt(t.minPlayers)||1,
          maxPlayers: t.maxPlayers ? parseInt(t.maxPlayers) : null,
          pricePerPerson: t.mode==='per_person' ? parseFloat(t.pricePerPerson)||null : null,
          priceFlat: t.mode==='flat' ? parseFloat(t.priceFlat)||null : null,
        }))
      }));
      await roomsApi.setPrices(room.id, payload);
      toast.success('Tarifs sauvegardés');
      onSaved();
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const unassigned = globalProfiles.filter(gp=>!profilePrices.find(p=>p.profileId===gp.id));

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <span style={{fontWeight:600,fontSize:14,display:'flex',alignItems:'center',gap:6}}><Tag size={14}/> Grille tarifaire</span>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          <Save size={12}/> {saving?'Sauvegarde…':'Sauvegarder'}
        </button>
      </div>

      {profilePrices.length===0 && <p className="text-sm text-muted" style={{marginBottom:12}}>Aucun tarif configuré.</p>}

      {profilePrices.map(profile=>(
        <div key={profile.profileId} style={{border:'1px solid var(--border)',borderRadius:10,padding:14,marginBottom:12,background:'var(--bg-base)'}}>
          {/* En-tête profil */}
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <span style={{fontWeight:700,fontSize:14,flex:1}}>{profile.profileName}</span>
            <button className="btn btn-ghost btn-sm" onClick={()=>addTier(profile.profileId)} style={{fontSize:12}}>
              <Plus size={12}/> Tranche
            </button>
            <button className="btn btn-ghost btn-sm" style={{padding:4}} onClick={()=>removeProfile(profile.profileId)}>
              <Trash2 size={13} color="var(--red)"/>
            </button>
          </div>

          {/* Tranches */}
          {profile.tiers.map((tier,idx)=>(
            <div key={idx} style={{display:'grid',gridTemplateColumns:'80px 80px 1fr auto auto',gap:8,alignItems:'center',marginBottom:8,padding:'8px 10px',background:'var(--bg-elevated)',borderRadius:8}}>
              {/* Min joueurs */}
              <div>
                <div className="text-xs text-muted" style={{marginBottom:3}}>De (joueurs)</div>
                <input type="number" min={1} max={20} className="input" value={tier.minPlayers}
                  onChange={e=>updateTier(profile.profileId,idx,'minPlayers',e.target.value)}/>
              </div>
              {/* Max joueurs */}
              <div>
                <div className="text-xs text-muted" style={{marginBottom:3}}>À (optionnel)</div>
                <input type="number" min={1} max={20} className="input" value={tier.maxPlayers}
                  placeholder="∞"
                  onChange={e=>updateTier(profile.profileId,idx,'maxPlayers',e.target.value)}/>
              </div>
              {/* Prix */}
              <div>
                <div style={{display:'flex',border:'1px solid var(--border)',borderRadius:6,overflow:'hidden',marginBottom:4}}>
                  {[['per_person','Par pers.'],['flat','Prix fixe']].map(([mode,label])=>(
                    <button key={mode} type="button"
                      onClick={()=>updateTier(profile.profileId,idx,'mode',mode)}
                      style={{flex:1,padding:'4px 0',border:'none',cursor:'pointer',fontSize:11,fontFamily:'inherit',
                        background:tier.mode===mode?'var(--accent)':'var(--bg-elevated)',
                        color:tier.mode===mode?'#fff':'var(--text-2)'}}>
                      {label}
                    </button>
                  ))}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <input type="number" step="0.01" min={0} className="input"
                    placeholder={tier.mode==='flat'?'Ex: 120.00':'Ex: 25.00'}
                    value={tier.mode==='flat'?tier.priceFlat:tier.pricePerPerson}
                    onChange={e=>updateTier(profile.profileId,idx,tier.mode==='flat'?'priceFlat':'pricePerPerson',e.target.value)}/>
                  <span className="text-xs text-muted" style={{whiteSpace:'nowrap'}}>
                    {tier.mode==='flat'?'€ fixe':'€/pers'}
                  </span>
                </div>
              </div>
              {/* Aperçu */}
              <div style={{textAlign:'right',fontSize:12,color:'var(--teal)',fontWeight:600,whiteSpace:'nowrap'}}>
                {tier.mode==='flat' && tier.priceFlat ? `${parseFloat(tier.priceFlat).toFixed(2)} €` :
                 tier.pricePerPerson ? `${parseFloat(tier.pricePerPerson).toFixed(2)} €/p` : '—'}
              </div>
              {/* Supprimer tranche */}
              {profile.tiers.length>1 && (
                <button type="button" className="btn btn-ghost btn-sm" style={{padding:4}}
                  onClick={()=>removeTier(profile.profileId,idx)}>
                  <Trash2 size={12} color="var(--text-3)"/>
                </button>
              )}
            </div>
          ))}

          {/* Aperçu dégressif */}
          {profile.tiers.length>1 && (
            <div style={{fontSize:11,color:'var(--text-3)',marginTop:6,padding:'6px 10px',background:'rgba(0,210,180,0.05)',borderRadius:6}}>
              {profile.tiers.map((t,i)=>{
                const price = t.mode==='flat'?t.priceFlat:t.pricePerPerson;
                const label = t.mode==='flat'?`${price||'?'}€ fixe`:`${price||'?'}€/pers`;
                const range = t.maxPlayers ? `${t.minPlayers}–${t.maxPlayers} j.` : `${t.minPlayers}+ j.`;
                return <span key={i} style={{marginRight:12}}>📋 {range} → {label}</span>;
              })}
            </div>
          )}
        </div>
      ))}

      {/* Ajouter profil existant */}
      {unassigned.length>0 && (
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
          {unassigned.map(p=>(
            <button key={p.id} className="btn btn-secondary btn-sm" onClick={()=>addProfile(p)}>
              <Plus size={11}/> {p.name}
            </button>
          ))}
        </div>
      )}

      {/* Créer nouveau profil */}
      {!showNewProfile ? (
        <button className="btn btn-ghost btn-sm" style={{fontSize:12}} onClick={()=>setShowNewProfile(true)}>
          <Plus size={12}/> Nouveau profil tarifaire…
        </button>
      ) : (
        <div style={{background:'var(--bg-elevated)',borderRadius:8,padding:12,marginTop:8}}>
          <div className="form-row" style={{marginBottom:8}}>
            <input className="input" placeholder="Nom du profil (ex: Groupe entreprise)"
              value={newProfileName} onChange={e=>setNewProfileName(e.target.value)}/>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary btn-sm" onClick={createAndAddProfile}><Plus size={12}/> Créer</button>
            <button className="btn btn-ghost btn-sm" onClick={()=>setShowNewProfile(false)}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Gestionnaire de créneaux ──────────────────────────────────
function SlotManager({ room, onSaved }) {
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [newDays, setNewDays] = useState([]); // [] = tous les jours

  useEffect(() => {
    setSlots((room.time_slots||[]).map(s=>({
      id:s.id, startTime:s.start_time?.slice(0,5), dayOfWeek:s.day_of_week||[]
    })));
  }, [room]);

  const addSlot = () => {
    if (!newTime) return toast.error('Heure requise');
    if (slots.find(s=>s.startTime===newTime)) return toast.error('Ce créneau existe déjà');
    setSlots(prev=>[...prev,{startTime:newTime,dayOfWeek:newDays}].sort((a,b)=>a.startTime.localeCompare(b.startTime)));
    setNewTime('');
  };

  const removeSlot = idx => setSlots(prev=>prev.filter((_,i)=>i!==idx));

  const toggleDay = (slotIdx, day) => {
    setSlots(prev=>prev.map((s,i)=>{
      if (i!==slotIdx) return s;
      const days = s.dayOfWeek||[];
      return {...s, dayOfWeek: days.includes(day) ? days.filter(d=>d!==day) : [...days,day]};
    }));
  };

  // Note : fonction actuellement non déclenchée par un bouton visible (code
  // orphelin) — le window.confirm() a été retiré comme partout ailleurs ;
  // si un bouton est un jour ajouté pour l'appeler, passer par ConfirmModal
  // (voir components/ConfirmModal.jsx) plutôt que de réintroduire un confirm() natif.
  const resetFromProfile = async (profileId) => {
    try {
      const r = await api.get('/payments/price-profiles/'+profileId+'/tiers');
      if (r.data.tiers?.length > 0) {
        const defaultTiers = r.data.tiers.map(t => ({
          minPlayers: t.min_players,
          maxPlayers: t.max_players || '',
          pricePerPerson: t.price_per_person || '',
          priceFlat: t.price_flat || '',
          mode: t.price_flat ? 'flat' : 'per_person',
        }));
        setProfilePrices(prev => prev.map(p =>
          p.profileId === profileId ? { ...p, tiers: defaultTiers } : p
        ));
        toast.success('Tarifs réinitialisés depuis le profil');
      }
    } catch { toast.error('Erreur'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/rooms/${room.id}/slots`, { slots: slots.map(s=>({
        startTime: s.startTime,
        dayOfWeek: s.dayOfWeek?.length>0 ? s.dayOfWeek : null,
      }))});
      toast.success('Créneaux sauvegardés');
      onSaved();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <span style={{fontWeight:600,fontSize:14,display:'flex',alignItems:'center',gap:6}}><Clock size={14}/> Créneaux horaires</span>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          <Save size={12}/> {saving?'Sauvegarde…':'Sauvegarder'}
        </button>
      </div>

      {slots.length===0 && <p className="text-sm text-muted" style={{marginBottom:12}}>Aucun créneau configuré.</p>}

      {/* Liste des créneaux */}
      <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
        {slots.map((slot,idx)=>(
          <div key={idx} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'var(--bg-elevated)',borderRadius:8}}>
            <span style={{fontFamily:'monospace',fontWeight:700,fontSize:15,minWidth:48}}>{slot.startTime}</span>
            <div style={{flex:1,display:'flex',gap:4,flexWrap:'wrap'}}>
              {DAYS.map((d,n)=>(
                <button key={n} type="button" onClick={()=>toggleDay(idx,n)}
                  style={{padding:'2px 8px',borderRadius:5,border:'1px solid var(--border)',cursor:'pointer',fontSize:11,fontFamily:'inherit',
                    background:(slot.dayOfWeek?.length===0||!slot.dayOfWeek?.length)?
                      (n===0||n===6?'rgba(245,166,35,0.15)':'rgba(124,92,252,0.1)') :
                      slot.dayOfWeek?.includes(n)?'var(--accent-glow)':'var(--bg-base)',
                    color:(slot.dayOfWeek?.length===0||!slot.dayOfWeek?.length)?
                      (n===0||n===6?'var(--amber)':'var(--accent-light)') :
                      slot.dayOfWeek?.includes(n)?'var(--accent-light)':'var(--text-3)',
                  }}>
                  {d}
                </button>
              ))}
              {(!slot.dayOfWeek||slot.dayOfWeek.length===0) && (
                <span className="text-xs text-muted" style={{alignSelf:'center',marginLeft:4}}>tous les jours</span>
              )}
            </div>
            <button className="btn btn-ghost btn-sm" style={{padding:4}} onClick={()=>removeSlot(idx)}>
              <Trash2 size={13} color="var(--red)"/>
            </button>
          </div>
        ))}
      </div>

      {/* Ajouter créneau */}
      <div style={{display:'flex',gap:8,alignItems:'center'}}>
        <input type="time" className="input" style={{width:120}} value={newTime} onChange={e=>setNewTime(e.target.value)}/>
        <button className="btn btn-secondary btn-sm" onClick={addSlot}><Plus size={13}/> Ajouter</button>
        <span className="text-xs text-muted">Les jours sont configurables après l'ajout</span>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function AdminRooms() {
  const { can, canCreate, features } = useAuth();
  if (!can('admin.rooms')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;

  const [rooms, setRooms]       = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState({}); // {roomId: 'prices'|'slots'|null}

  const load = useCallback(()=>roomsApi.list().then(r=>setRooms(r.data.rooms)),[]);
  useEffect(()=>{load();},[load]);

  const togglePanel = (roomId, panel) =>
    setExpanded(prev=>({...prev,[roomId]: prev[roomId]===panel ? null : panel}));

  return (
    <>
      <div className="page-header">
        <div><h2>Salles & Tarifs</h2><p>Configuration des salles, grilles tarifaires et créneaux horaires</p></div>
        {(() => {
          const roomCount = rooms?.filter(r => r.is_active).length || 0;
          const ok = canCreate('max_rooms', roomCount);
          return (
            <button className="btn btn-primary"
              onClick={() => { if (ok) { setSelected(null); setShowForm(true); } }}
              disabled={!ok}
              title={!ok ? `Limite de salles atteinte pour votre plan` : ''}>
              <Plus size={15}/> Nouvelle salle
            </button>
          );
        })()}
      </div>

      <div className="page-body">
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {rooms.map(room=>(
            <div key={room.id} className="card" style={{padding:0,overflow:'hidden'}}>
              {/* Ligne principale */}
              <div style={{display:'flex',alignItems:'center',gap:16,padding:'18px 20px'}}>
                {room.image_url
                  ? <img src={room.image_url} alt={room.name} style={{width:64,height:64,borderRadius:10,objectFit:'cover',flexShrink:0}}/>
                  : <div style={{width:64,height:64,borderRadius:10,background:'linear-gradient(135deg,var(--accent-glow),var(--teal-glow))',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><BrandIcon size={28}/></div>
                }
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                    <h3 style={{fontSize:16,fontWeight:700}}>{room.name}</h3>
                    {!room.is_active && <span className="badge badge-red">Désactivée</span>}
                  </div>
                  <p className="text-sm text-muted" style={{marginBottom:6,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {room.description||'—'}
                  </p>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <span className="badge badge-purple">{room.duration_min} min</span>
                    <span className="badge badge-teal">{room.min_players}–{room.max_players} joueurs</span>
                    <span className="badge badge-amber">{'★'.repeat(room.difficulty||0)}{'☆'.repeat(5-(room.difficulty||0))}</span>
                    <span className="badge badge-gray">{(room.prices||[]).length} profil(s)</span>
                    <span className="badge badge-gray">{(room.time_slots||[]).length} créneau(x)</span>
                  </div>
                </div>
                <div style={{display:'flex',gap:8,flexShrink:0}}>
                  <button className="btn btn-secondary btn-sm" onClick={()=>{setSelected(room);setShowForm(true);}}>
                    <Edit2 size={13}/> Infos
                  </button>
                  <button className={`btn btn-sm ${expanded[room.id]==='prices'?'btn-teal':'btn-secondary'}`}
                    onClick={()=>togglePanel(room.id,'prices')}>
                    <Tag size={14}/> Tarifs {expanded[room.id]==='prices'?<ChevronUp size={13}/>:<ChevronDown size={13}/>}
                  </button>
                  <button className={`btn btn-sm ${expanded[room.id]==='slots'?'btn-teal':'btn-secondary'}`}
                    onClick={()=>togglePanel(room.id,'slots')}>
                    <Clock size={14}/> Créneaux {expanded[room.id]==='slots'?<ChevronUp size={13}/>:<ChevronDown size={13}/>}
                  </button>
                </div>
              </div>

              {/* Résumé tarifs collapsed */}
              {!expanded[room.id] && (room.prices||[]).length>0 && (
                <div style={{padding:'0 20px 14px',display:'flex',gap:8,flexWrap:'wrap'}}>
                  {(room.prices||[]).map(p=>(
                    <div key={p.price_profile_id} style={{background:'var(--bg-elevated)',borderRadius:8,padding:'5px 12px',fontSize:13}}>
                      <span style={{color:'var(--text-2)',marginRight:6}}>{p.profile_name}</span>
                      {(p.tiers||[]).map((t,i)=>(
                        <span key={i} style={{color:'var(--teal)',fontWeight:600,marginRight:4}}>
                          {t.min_players}{t.max_players?`–${t.max_players}`:'+'} j. → {
                            t.price_flat ? `${parseFloat(t.price_flat).toFixed(2)}€` : `${parseFloat(t.price_per_person).toFixed(2)}€/p`
                          }
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Résumé créneaux collapsed */}
              {!expanded[room.id] && (room.time_slots||[]).length>0 && (
                <div style={{padding:'0 20px 14px',display:'flex',gap:6,flexWrap:'wrap'}}>
                  {(room.time_slots||[]).map(s=>(
                    <span key={s.id} className="badge badge-purple" style={{fontFamily:'monospace'}}>
                      {s.start_time?.slice(0,5)}
                    </span>
                  ))}
                </div>
              )}

              {/* Panel tarifs */}
              {expanded[room.id]==='prices' && (
                <div style={{padding:'0 20px 20px',borderTop:'1px solid var(--border)',paddingTop:16}}>
                  <PriceManager room={room} onSaved={load}/>
                </div>
              )}

              {/* Panel créneaux */}
              {expanded[room.id]==='slots' && (
                <div style={{padding:'0 20px 20px',borderTop:'1px solid var(--border)',paddingTop:16}}>
                  <SlotManager room={room} onSaved={load}/>
                </div>
              )}
            </div>
          ))}
          {rooms.length===0&&<div className="empty-state card"><p>Aucune salle configurée</p></div>}
        </div>
      </div>

      {showForm&&<RoomFormModal selected={selected} onClose={()=>{setShowForm(false);setSelected(null);}} onSaved={load}/>}
    </>
  );
}
